import Phaser from 'phaser';
import { WorldState } from '../sim/world-state';
import { spawnAlly, spawnInitialFlock } from '../sim/spawn';
import { flockingSystem } from '../sim/systems/flocking';
import { combatSystem } from '../sim/systems/combat';
import { lifecycleSystem } from '../sim/systems/lifecycle';
import { pickupsSystem } from '../sim/systems/pickups';
import { WaveDirector } from '../sim/systems/wave-director';
import { RunRenderer, screenToWorld } from '../render/run-renderer';
import { C } from '../sim/components';
import type { FactionComp, GenomeComp, EmergentComp, PickupComp, Health } from '../sim/components';
import { BreedingPanel, collectFertileCandidates, autoPickPair } from '../ui/breeding-panel';
import type { BreedingPanelResult, BreedingPanelFallback } from '../ui/breeding-panel';
import { OnboardingOverlay } from '../ui/onboarding';
import { DebugOverlay } from '../ui/debug-overlay';
import { PauseMenu } from '../ui/pause-menu';
import { breed } from '../sim/breeding';
import { randomGenome } from '../sim/genome';
import type { Genome } from '../sim/genome';
import { audio } from '../audio';
import { addDiscovery } from '../storage/journal';
import { currentPhase } from '../sim/systems/wave-director';
import type { Phase } from '../sim/systems/wave-director';
import { EMERGENT_TRAITS } from '../sim/traits/emergent';
import { isTouchDevice, markTouchInput } from '../input/device';

const FIXED_DT = 1 / 60;
const MAX_SUB_STEPS = 5;
/** Touch device gets a smaller starting flock + lighter wave budget. */
const TOUCH_INITIAL_FLOCK = 8;
const DESKTOP_INITIAL_FLOCK = 12;
const TOUCH_WAVE_MULTIPLIER = 0.65;

export class RunScene extends Phaser.Scene {
  private ws!: WorldState;
  private gfx!: RunRenderer;
  private director!: WaveDirector;
  private panel!: BreedingPanel;
  private pauseMenu!: PauseMenu;
  private onboarding!: OnboardingOverlay;
  private debug!: DebugOverlay;
  private accumulator = 0;
  private hudText!: Phaser.GameObjects.Text;
  private pauseBtn!: Phaser.GameObjects.Container;
  private startMs = 0;
  private paused = false;
  private pendingPickups: { x: number; y: number }[] = [];
  private autoBreedRemaining = 0;

  // Onboarding state.
  private moved = false;
  private collectedAny = false;
  private emergentSeen = new Set<string>();

  // Phase-transition tracking (task #8).
  private lastPhase: Phase = 'early';

  // Emergent-trait toast queue (task #9).
  private toastQueue: string[] = [];
  private toastShowing = false;

  // SFX delta tracking.
  private lastKillCount = 0;
  private lastHitCount = 0;
  private lastAllyDeathCount = 0;
  private lastPickupCount = 0;

  // Particle emitters for death bursts.
  private enemyBurst!: Phaser.GameObjects.Particles.ParticleEmitter;
  private allyBurst!: Phaser.GameObjects.Particles.ParticleEmitter;

  constructor() {
    super({ key: 'RunScene' });
  }

  create(data?: { presetGenomes?: Genome[] }): void {
    const seed = Date.now() & 0x7fffffff;
    this.ws = new WorldState(seed);
    this.gfx = new RunRenderer(this);
    const touch = isTouchDevice();
    this.director = new WaveDirector({
      budgetMultiplier: touch ? TOUCH_WAVE_MULTIPLIER : 1,
    });
    this.panel = new BreedingPanel(this);
    this.pauseMenu = new PauseMenu(this);
    this.onboarding = new OnboardingOverlay(this);
    this.debug = new DebugOverlay(this);
    this.accumulator = 0;
    this.pendingPickups = [];
    this.paused = false;
    this.moved = false;
    this.collectedAny = false;
    this.emergentSeen = new Set();
    this.startMs = this.time.now;

    if (data?.presetGenomes && data.presetGenomes.length > 0) {
      this.spawnPresetFlock(data.presetGenomes);
    } else {
      spawnInitialFlock(this.ws, touch ? TOUCH_INITIAL_FLOCK : DESKTOP_INITIAL_FLOCK, 0, 0);
    }
    this.recordCurrentEmergents();
    this.buildParticleEmitters();

    this.hudText = this.add
      .text(12, 8, '', {
        fontFamily: 'system-ui',
        fontSize: '14px',
        color: '#e6e6e6',
      })
      .setScrollFactor(0)
      .setDepth(1000);

    this.buildPauseButton();

    // Pointer handlers: any pointer event updates the cursor. Touch events
    // also flip the global touch-mode flag.
    this.input.on('pointermove', (p: Phaser.Input.Pointer) => {
      if (p.wasTouch) markTouchInput();
      const w = screenToWorld(this, p.x, p.y);
      this.ws.cursor.x = w.x;
      this.ws.cursor.y = w.y;
      this.moved = true;
    });
    this.input.on('pointerdown', (p: Phaser.Input.Pointer) => {
      if (p.wasTouch) markTouchInput();
      const w = screenToWorld(this, p.x, p.y);
      this.ws.cursor.x = w.x;
      this.ws.cursor.y = w.y;
      this.moved = true;
    });

    const { width, height } = this.scale.gameSize;
    this.ws.cursor.x = 0;
    this.ws.cursor.y = 0;
    this.input.activePointer.x = width / 2;
    this.input.activePointer.y = height / 2;

    this.input.keyboard?.on('keydown-ESC', () => {
      if (this.paused) return;
      this.openPauseMenu();
    });

    this.input.keyboard?.on('keydown-M', () => {
      audio.toggleMute();
    });

    this.lastKillCount = this.ws.killCount;
    this.lastHitCount = this.ws.hitCount;
    this.lastAllyDeathCount = this.ws.allyDeathCount;
    this.lastPickupCount = this.ws.world.store<PickupComp>(C.pickup).size;

    audio.startMusic(currentPhase(this.ws.time));

    // Reposition pause button if the browser viewport changes (mobile
    // address-bar collapse/expand).
    this.scale.on('resize', () => this.layoutPauseButton());
  }

  update(_time: number, deltaMs: number): void {
    const dt = Math.min(deltaMs / 1000, 0.1);

    if (!this.paused) {
      this.accumulator += dt;
      const simStart = performance.now();
      let steps = 0;
      while (this.accumulator >= FIXED_DT && steps < MAX_SUB_STEPS) {
        this.tickSim(FIXED_DT);
        this.accumulator -= FIXED_DT;
        steps++;
      }
      if (steps === MAX_SUB_STEPS) this.accumulator = 0;
      this.debug.recordSimMs(performance.now() - simStart);

      if (this.pendingPickups.length > 0 && !this.panel.isOpen()) {
        const { x, y } = this.pendingPickups.shift()!;
        if (this.autoBreedRemaining > 0) {
          this.autoBreedRemaining--;
          this.doAutoBreed(x, y);
        } else {
          this.openBreedingPanel(x, y);
        }
      }
    }

    this.gfx.draw(this.ws);
    this.drainDeathEvents();
    this.updateHud();
    this.onboarding.update({
      moved: this.moved,
      killed: this.ws.killCount > 0,
      collected: this.collectedAny,
      bred: this.ws.breedCount > 0,
    });
    this.debug.draw(this.ws);
    this.playSfxFromDeltas();
    audio.setMusicPhase(currentPhase(this.ws.time));

    // Task #8: phase transition announcements.
    const phase = currentPhase(this.ws.time);
    if (phase !== this.lastPhase) {
      this.showPhaseAnnouncement(phase);
      this.lastPhase = phase;
    }

    if (this.allyCount() === 0 && !this.paused) {
      const stats = this.runStats();
      for (const tid of this.emergentSeen) addDiscovery(tid);
      this.scene.start('GameOverScene', { stats });
    }
  }

  private tickSim(dt: number): void {
    this.ws.time += dt;
    flockingSystem(this.ws, dt);
    combatSystem(this.ws, dt);
    lifecycleSystem(this.ws, dt);
    pickupsSystem(this.ws, dt, {
      onCollected: (x, y) => {
        this.pendingPickups.push({ x, y });
        this.collectedAny = true;
        audio.play('pickupCollect');
      },
    });
    this.director.update(this.ws, dt);
  }

  private playSfxFromDeltas(): void {
    if (this.ws.hitCount > this.lastHitCount) {
      audio.play('hit');
      this.lastHitCount = this.ws.hitCount;
    }
    if (this.ws.killCount > this.lastKillCount) {
      audio.play('kill');
      this.lastKillCount = this.ws.killCount;
    }
    if (this.ws.allyDeathCount > this.lastAllyDeathCount) {
      audio.play('ballDeath');
      this.cameras.main.shake(120, 0.005);
      this.lastAllyDeathCount = this.ws.allyDeathCount;
    }
    const pickups = this.ws.world.store<PickupComp>(C.pickup).size;
    if (pickups > this.lastPickupCount) audio.play('pickupDrop');
    this.lastPickupCount = pickups;
  }

  private async openBreedingPanel(x: number, y: number): Promise<void> {
    this.paused = true;
    const result = await this.panel.open(this.ws);
    this.paused = false;
    if (!result) return;
    if ((result as BreedingPanelFallback).type === 'fallback') {
      this.applyBreedFallback(x, y);
      return;
    }
    const breedResult = result as BreedingPanelResult;
    if (breedResult.autoQueue) this.autoBreedRemaining = breedResult.autoQueue;
    this.applyBreed(breedResult, x, y);
  }

  /** Fallback when fewer than 2 fertile parents: spawn a random ally and heal all. */
  private applyBreedFallback(x: number, y: number): void {
    const rng = this.ws.rng('breeding');
    spawnAlly(this.ws, { genome: randomGenome(rng, 25), x, y });
    const factions = this.ws.world.store<FactionComp>(C.faction);
    const healths = this.ws.world.store<Health>(C.health);
    healths.forEach((h, id) => {
      if (factions.get(id)?.side !== 'ally') return;
      h.current = h.max;
    });
    audio.play('breed');
  }

  private doAutoBreed(x: number, y: number): void {
    const result = autoPickPair(collectFertileCandidates(this.ws));
    if (result) this.applyBreed(result, x, y);
  }

  private applyBreed(result: BreedingPanelResult, x: number, y: number): void {
    const genomes = this.ws.world.store<GenomeComp>(C.genome);
    const emergents = this.ws.world.store<EmergentComp>(C.emergent);
    const a = genomes.get(result.parentAId);
    const b = genomes.get(result.parentBId);
    if (!a || !b) return;

    const extraA = emergents.get(result.parentAId)?.effect.extraOffspring ?? 0;
    const extraB = emergents.get(result.parentBId)?.effect.extraOffspring ?? 0;
    const offspringCount = 1 + extraA + extraB;
    const rng = this.ws.rng('breeding');
    for (let i = 0; i < offspringCount; i++) {
      const child = breed(a.genome, b.genome, rng);
      const angle = (i / offspringCount) * Math.PI * 2;
      const radius = i === 0 ? 0 : 14;
      spawnAlly(this.ws, {
        genome: child,
        x: x + Math.cos(angle) * radius,
        y: y + Math.sin(angle) * radius,
      });
    }
    this.ws.breedCount += 1;
    // Task #9: detect newly-unlocked emergent traits and queue toasts.
    const seenBefore = new Set(this.emergentSeen);
    this.recordCurrentEmergents();
    for (const tid of this.emergentSeen) {
      if (!seenBefore.has(tid)) this.toastQueue.push(tid);
    }
    if (this.toastQueue.length > 0 && !this.toastShowing) this.showNextToast();
    audio.play('breed');
  }

  private async openPauseMenu(): Promise<void> {
    if (this.pauseMenu.isOpen()) return;
    this.paused = true;
    const action = await this.pauseMenu.open();
    this.paused = false;
    if (action === 'endRun') {
      this.scene.start('GameOverScene', { stats: this.runStats() });
    }
  }

  private buildPauseButton(): void {
    const c = this.add.container(0, 0).setDepth(1000);
    const w = 92;
    const h = isTouchDevice() ? 44 : 32;
    const bg = this.add
      .rectangle(0, 0, w, h, 0x1a1d23, 0.8)
      .setOrigin(0, 0)
      .setStrokeStyle(1, 0x6a6f7a);
    const t = this.add
      .text(w / 2, h / 2, '⏸ pause', {
        fontFamily: 'system-ui',
        fontSize: isTouchDevice() ? '15px' : '13px',
        color: '#e6e6e6',
      })
      .setOrigin(0.5);
    bg.setInteractive({ useHandCursor: true });
    bg.on('pointerover', () => bg.setFillStyle(0x2a2d33, 0.9));
    bg.on('pointerout', () => bg.setFillStyle(0x1a1d23, 0.8));
    bg.on(
      'pointerup',
      (_p: Phaser.Input.Pointer, _lx: number, _ly: number, ev: Phaser.Types.Input.EventData) => {
        ev.stopPropagation();
        this.openPauseMenu();
      },
    );
    c.add([bg, t]);
    this.pauseBtn = c;
    this.layoutPauseButton();
  }

  private layoutPauseButton(): void {
    if (!this.pauseBtn) return;
    const { width } = this.scale.gameSize;
    const w = (this.pauseBtn.list[0] as Phaser.GameObjects.Rectangle).width;
    this.pauseBtn.setPosition(width - w - 12, 8);
  }

  private recordCurrentEmergents(): void {
    const factions = this.ws.world.store<FactionComp>(C.faction);
    const emergents = this.ws.world.store<EmergentComp>(C.emergent);
    emergents.forEach((e, id) => {
      if (factions.get(id)?.side !== 'ally') return;
      for (const tid of e.ids) this.emergentSeen.add(tid);
    });
  }

  private updateHud(): void {
    const elapsedSec = Math.floor((this.time.now - this.startMs) / 1000);
    const mm = String(Math.floor(elapsedSec / 60)).padStart(2, '0');
    const ss = String(elapsedSec % 60).padStart(2, '0');
    const fps = Math.round(this.game.loop.actualFps);
    const touch = isTouchDevice();
    const hint = this.paused
      ? '[PAUSED]'
      : touch
        ? 'tap pause for menu'
        : `ESC = pause    \` = debug    M = ${audio.isMuted() ? 'unmute' : 'mute'}`;
    const autoLabel = this.autoBreedRemaining > 0 ? `    auto ×${this.autoBreedRemaining}` : '';
    this.hudText.setText(
      [
        `${mm}:${ss}    fps ${fps}`,
        `allies ${this.allyCount()}    enemies ${this.enemyCount()}`,
        `kills ${this.ws.killCount}    breeds ${this.ws.breedCount}${autoLabel}`,
        hint,
      ].join('\n'),
    );
  }

  private allyCount(): number {
    let n = 0;
    this.ws.world.store<FactionComp>(C.faction).forEach((f) => {
      if (f.side === 'ally') n++;
    });
    return n;
  }

  private enemyCount(): number {
    let n = 0;
    this.ws.world.store<FactionComp>(C.faction).forEach((f) => {
      if (f.side === 'enemy') n++;
    });
    return n;
  }

  private buildParticleEmitters(): void {
    // Tiny white-dot texture for particles.
    const pc = document.createElement('canvas');
    pc.width = 6;
    pc.height = 6;
    const pCtx = pc.getContext('2d')!;
    pCtx.fillStyle = 'white';
    pCtx.beginPath();
    pCtx.arc(3, 3, 3, 0, Math.PI * 2);
    pCtx.fill();
    this.textures.addCanvas('__particle_dot', pc);

    const base = {
      speed: { min: 35, max: 110 },
      angle: { min: 0, max: 360 },
      scale: { start: 0.55, end: 0 },
      alpha: { start: 1, end: 0 },
      lifespan: 380,
      emitting: false,
    };

    this.enemyBurst = this.add
      .particles(0, 0, '__particle_dot', {
        ...base,
        tint: [0xff6622, 0xffaa33, 0xffee55],
      })
      .setDepth(25);

    this.allyBurst = this.add
      .particles(0, 0, '__particle_dot', {
        ...base,
        tint: [0xff99cc, 0xffffff, 0xaaddff],
      })
      .setDepth(25);
  }

  private drainDeathEvents(): void {
    if (this.ws.deathEvents.length === 0) return;
    const { width, height } = this.scale.gameSize;
    for (const ev of this.ws.deathEvents) {
      const sx = width / 2 + ev.x;
      const sy = height / 2 + ev.y;
      const count = ev.faction === 'enemy' ? 10 : 7;
      if (ev.faction === 'enemy') {
        this.enemyBurst.explode(count, sx, sy);
      } else {
        this.allyBurst.explode(count, sx, sy);
      }
    }
    this.ws.deathEvents.length = 0;
  }

  /** Task #11: spawn preset genomes in a circle (mirrors spawnInitialFlock). */
  private spawnPresetFlock(genomes: Genome[]): void {
    const count = genomes.length;
    for (let i = 0; i < count; i++) {
      const angle = (i / count) * Math.PI * 2;
      const radius = 60;
      spawnAlly(this.ws, {
        genome: genomes[i]!,
        x: Math.cos(angle) * radius,
        y: Math.sin(angle) * radius,
      });
    }
  }

  /** Task #8: centered announcement banner on phase transition. */
  private showPhaseAnnouncement(phase: Phase): void {
    const messages: Partial<Record<Phase, string>> = {
      mid: 'Phase 2 — Shooters incoming',
      late: 'Phase 3 — Elites incoming',
    };
    const msg = messages[phase];
    if (!msg) return;
    const { width, height } = this.scale.gameSize;
    const text = this.add
      .text(width / 2, height / 2, msg, {
        fontFamily: 'system-ui',
        fontSize: '28px',
        fontStyle: 'bold',
        color: '#ffffff',
        backgroundColor: 'rgba(0,0,0,0.6)',
        padding: { x: 20, y: 12 },
      })
      .setOrigin(0.5)
      .setAlpha(0)
      .setDepth(1500)
      .setScrollFactor(0);
    this.tweens.add({
      targets: text,
      alpha: 1,
      duration: 300,
      onComplete: () => {
        this.time.delayedCall(2000, () => {
          this.tweens.add({
            targets: text,
            alpha: 0,
            duration: 500,
            onComplete: () => text.destroy(),
          });
        });
      },
    });
  }

  /** Task #9: show emergent-trait discovery toasts one at a time. */
  private showNextToast(): void {
    const traitId = this.toastQueue.shift();
    if (!traitId) {
      this.toastShowing = false;
      return;
    }
    this.toastShowing = true;
    const trait = EMERGENT_TRAITS.find((t) => t.id === traitId);
    if (!trait) {
      this.showNextToast();
      return;
    }
    const { width, height } = this.scale.gameSize;
    const msg = `New trait: ${trait.label}! — ${trait.description}`;
    const text = this.add
      .text(width / 2, height - 60, msg, {
        fontFamily: 'system-ui',
        fontSize: '15px',
        color: '#ff9ec4',
        backgroundColor: 'rgba(12,13,16,0.8)',
        padding: { x: 12, y: 6 },
      })
      .setOrigin(0.5, 1)
      .setAlpha(0)
      .setDepth(2000)
      .setScrollFactor(0);
    this.tweens.add({
      targets: text,
      alpha: 1,
      duration: 200,
      onComplete: () => {
        this.time.delayedCall(3000, () => {
          this.tweens.add({
            targets: text,
            alpha: 0,
            duration: 500,
            onComplete: () => {
              text.destroy();
              this.showNextToast();
            },
          });
        });
      },
    });
  }

  private runStats(): {
    time: number;
    kills: number;
    breeds: number;
    bestEmergent: string[];
  } {
    this.recordCurrentEmergents();
    return {
      time: Math.floor((this.time.now - this.startMs) / 1000),
      kills: this.ws.killCount,
      breeds: this.ws.breedCount,
      bestEmergent: [...this.emergentSeen],
    };
  }
}
